import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Users, Pencil, Save, Factory } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerMaster = () => {
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [plantAssignDialog, setPlantAssignDialog] = useState({ open: false, customer: null, selectedPlants: [] });
  const [newCustomer, setNewCustomer] = useState({ name: '', code: '', contact_email: '', plant_ids: [] });

  useEffect(() => {
    fetchCustomers();
    fetchPlants();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchPlants = async () => {
    try {
      const response = await axios.get(`${API}/plants`);
      setPlants(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Error fetching plants:', error);
    }
  };

  const handleCreate = async () => {
    if (!newCustomer.name || !newCustomer.code) {
      toast.error('Name and code are required');
      return;
    }
    if (!newCustomer.plant_ids || newCustomer.plant_ids.length === 0) {
      toast.error('Please assign the customer to at least one plant');
      return;
    }

    try {
      await axios.post(`${API}/customers`, newCustomer);
      toast.success('Customer created successfully');
      setNewCustomer({ name: '', code: '', contact_email: '', plant_ids: [] });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to create customer');
    }
  };

  const openPlantAssignDialog = (customer) => {
    setPlantAssignDialog({
      open: true,
      customer,
      selectedPlants: [...(customer.plant_ids || [])]
    });
  };

  const handlePlantToggle = (plantId) => {
    setPlantAssignDialog(prev => {
      const selected = prev.selectedPlants.includes(plantId)
        ? prev.selectedPlants.filter(id => id !== plantId)
        : [...prev.selectedPlants, plantId];
      return { ...prev, selectedPlants: selected };
    });
  };

  const handleSavePlantAssignment = async () => {
    try {
      await axios.post(`${API}/customers/${plantAssignDialog.customer.id}/assign-plants`, {
        plant_ids: plantAssignDialog.selectedPlants
      });
      toast.success(`Customer assigned to ${plantAssignDialog.selectedPlants.length} plant(s)`);
      setPlantAssignDialog({ open: false, customer: null, selectedPlants: [] });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign plants');
    }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="customer-create-card">
        <CardHeader>
          <CardTitle>Add New Customer</CardTitle>
          <CardDescription>Register a new customer for demand planning</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Customer Name *</label>
              <Input
                data-testid="input-customer-name"
                placeholder="Maruti Suzuki"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Code *</label>
              <Input
                data-testid="input-customer-code"
                placeholder="MSIL"
                value={newCustomer.code}
                onChange={(e) => setNewCustomer({ ...newCustomer, code: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Contact Email</label>
              <Input
                data-testid="input-customer-email"
                type="email"
                placeholder="orders@maruti.com"
                value={newCustomer.contact_email}
                onChange={(e) => setNewCustomer({ ...newCustomer, contact_email: e.target.value })}
              />
            </div>
            {/* Plant Assignment for Create */}
            <div>
              <label className="text-sm font-medium mb-2 block">Assign to Plants *</label>
              <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto bg-white">
                {plants.map(plant => (
                  <label key={plant.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <Checkbox
                      data-testid={`create-customer-plant-${plant.id}`}
                      checked={newCustomer.plant_ids.includes(plant.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewCustomer(prev => ({ ...prev, plant_ids: [...prev.plant_ids, plant.id] }));
                        } else {
                          setNewCustomer(prev => ({ ...prev, plant_ids: prev.plant_ids.filter(id => id !== plant.id) }));
                        }
                      }}
                    />
                    <span className="text-sm">{plant.code} - {plant.name}</span>
                  </label>
                ))}
              </div>
              {newCustomer.plant_ids.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">Select at least one plant</p>
              )}
            </div>
          </div>
          <Button data-testid="create-customer-btn" onClick={handleCreate} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Create Customer
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="customer-list-card">
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              All Customers ({customers.length})
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Plants</TableHead>
                <TableHead>Contact Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                    No customers found for this plant.
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <TableCell className="font-medium">{customer.name}</TableCell>
                    <TableCell>{customer.code}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {customer.plant_ids && customer.plant_ids.length > 0 ? (
                          customer.plant_ids.map(pid => {
                            const plant = plants.find(p => p.id === pid);
                            return plant ? (
                              <Badge key={pid} variant="outline" className="bg-teal-50 text-teal-700 text-xs whitespace-nowrap">
                                {plant.code}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-600 text-xs">None</Badge>
                        )}
                        <button
                          onClick={() => openPlantAssignDialog(customer)}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 hover:bg-blue-100 text-slate-500 hover:text-blue-600 transition-colors ml-0.5"
                          title="Manage plant assignment"
                          data-testid={`assign-plant-customer-${customer.id}`}
                        >
                          <Pencil className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>{customer.contact_email || 'N/A'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Plant Assignment Dialog */}
      <Dialog open={plantAssignDialog.open} onOpenChange={(open) => !open && setPlantAssignDialog({ open: false, customer: null, selectedPlants: [] })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-teal-600" />
              Assign Customer to Plants
            </DialogTitle>
            <DialogDescription>
              {plantAssignDialog.customer && (
                <span>Select plants for <strong>{plantAssignDialog.customer.name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {plants.map(plant => (
              <label
                key={plant.id}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                data-testid={`customer-assign-checkbox-${plant.id}`}
              >
                <Checkbox
                  checked={plantAssignDialog.selectedPlants.includes(plant.id)}
                  onCheckedChange={() => handlePlantToggle(plant.id)}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">{plant.name}</div>
                  <div className="text-xs text-slate-500">Code: {plant.code} | {plant.location}</div>
                </div>
                <Badge variant="outline" className="text-xs font-mono">{plant.code}</Badge>
              </label>
            ))}
            {plantAssignDialog.selectedPlants.length === 0 && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                Warning: Customer will not be visible in any plant if no plants are selected.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlantAssignDialog({ open: false, customer: null, selectedPlants: [] })}>
              Cancel
            </Button>
            <Button
              data-testid="save-customer-plant-assignment"
              onClick={handleSavePlantAssignment}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save ({plantAssignDialog.selectedPlants.length} plants)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerMaster;
